import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import {
  useSaveCareerLessonMutation,
  useAttachCareerLessonFileMutation,
  useDetachCareerLessonFileMutation,
  useRemoveCareerLessonVideoMutation,
  useSaveCareerLessonQuizMutation,
  CareerLessonInput,
  CareerQuizQuestionInput,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import {
  careerErrorMessage,
  ICareerLesson,
  ICareerLessonMedia,
  ICareerQuizQuestion,
} from './careerModel';

interface ICareerLessonMutationsHook {
  /**
   * Create when `id` is omitted, update when it is given. Resolves to the saved
   * lesson (freshly minted with an `id` on create) so the caller can rebind the
   * form to it and unlock the video/attachment controls — or `null` on failure.
   */
  saveLesson: (
    input: CareerLessonInput,
    id?: string,
  ) => Promise<ICareerLesson | null>;
  /** Attach one file; resolves to the lesson's updated media, or `null`. */
  addAttachment: (
    lessonId: string,
    file: File,
  ) => Promise<ICareerLessonMedia | null>;
  /** Detach one attachment by id; resolves to `true` on success. */
  removeAttachment: (attachmentId: string) => Promise<boolean>;
  /** Clear the lesson's video; resolves to the updated media, or `null`. */
  clearVideo: (lessonId: string) => Promise<ICareerLessonMedia | null>;
  /**
   * Replace the whole quiz for a lesson; resolves to the saved question set (as
   * the backend renumbered it) or `null` on failure. The backend rejects a
   * question with <2 options or 0 correct as a `customErrors` entry, surfaced
   * here as a toast.
   */
  saveQuiz: (
    lessonId: string,
    questions: CareerQuizQuestionInput[],
  ) => Promise<ICareerQuizQuestion[] | null>;
  /** In-flight flag for `saveLesson` (drives the form's Save button). */
  pending: boolean;
  /** In-flight flag for the media actions (attach/detach/clear video). */
  mediaPending: boolean;
  /** In-flight flag for `saveQuiz` (drives the quiz section's Save button). */
  quizPending: boolean;
}

/**
 * Spec 0018 + 0020 — lesson CRUD and media for the Обучение tab
 * (`upsertCareerLesson`, `addCareerLessonAttachment`,
 * `removeCareerLessonAttachment`, `clearCareerLessonVideo`).
 *
 * `order` is unique backend-side, so a colliding order comes back as a
 * `customErrors` entry rather than throwing — hence the envelope check before
 * the catch. Media mutations mirror the same envelope discipline.
 */
export const useCareerLessonMutations = (
  onChanged?: () => void,
): ICareerLessonMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const client = useApolloClient();
  const [saveRun] = useSaveCareerLessonMutation();
  const [attachRun] = useAttachCareerLessonFileMutation();
  const [detachRun] = useDetachCareerLessonFileMutation();
  const [clearVideoRun] = useRemoveCareerLessonVideoMutation();
  const [saveQuizRun] = useSaveCareerLessonQuizMutation();
  const [pending, setPending] = useState(false);
  const [mediaPending, setMediaPending] = useState(false);
  const [quizPending, setQuizPending] = useState(false);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.CareerCenter,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const saveLesson: ICareerLessonMutationsHook['saveLesson'] = useCallback(
    async (input, id) => {
      setPending(true);
      try {
        const result = await saveRun({ variables: { id, input } });
        const payload = result.data?.upsertCareerLesson;
        if (payload?.success && payload.lesson) {
          onChanged?.();
          return payload.lesson;
        }
        toastError(
          t('pages.careerCenter.lessons.toastSaveError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.lessons.toastSaveError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [saveRun, onChanged, toastError, t],
  );

  const addAttachment: ICareerLessonMutationsHook['addAttachment'] = useCallback(
    async (lessonId, file) => {
      setMediaPending(true);
      try {
        const result = await attachRun({ variables: { lessonId, file } });
        const payload = result.data?.addCareerLessonAttachment;
        if (payload?.success && payload.lesson) {
          onChanged?.();
          return {
            videoUrl: payload.lesson.videoUrl ?? null,
            attachments: payload.lesson.attachments,
          };
        }
        toastError(
          t('pages.careerCenter.lessons.toastAttachmentError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.lessons.toastAttachmentError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setMediaPending(false);
      }
    },
    [attachRun, onChanged, toastError, t],
  );

  const removeAttachment: ICareerLessonMutationsHook['removeAttachment'] = useCallback(
    async (attachmentId) => {
      setMediaPending(true);
      try {
        const result = await detachRun({ variables: { attachmentId } });
        const payload = result.data?.removeCareerLessonAttachment;
        if (payload?.success) {
          // The mutation returns only `ok` (no lesson), so evict the entity so
          // any cached lesson's `attachments` list drops the dangling ref and a
          // reopened form doesn't show the removed file again.
          client.cache.evict({
            id: client.cache.identify({
              __typename: 'CareerLessonAttachmentType',
              id: attachmentId,
            }),
          });
          client.cache.gc();
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.careerCenter.lessons.toastAttachmentRemoveError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.lessons.toastAttachmentRemoveError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setMediaPending(false);
      }
    },
    [detachRun, client, onChanged, toastError, t],
  );

  const clearVideo: ICareerLessonMutationsHook['clearVideo'] = useCallback(
    async (lessonId) => {
      setMediaPending(true);
      try {
        const result = await clearVideoRun({ variables: { lessonId } });
        const payload = result.data?.clearCareerLessonVideo;
        if (payload?.success && payload.lesson) {
          onChanged?.();
          return {
            videoUrl: payload.lesson.videoUrl ?? null,
            attachments: payload.lesson.attachments,
          };
        }
        toastError(
          t('pages.careerCenter.lessons.toastVideoRemoveError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.lessons.toastVideoRemoveError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setMediaPending(false);
      }
    },
    [clearVideoRun, onChanged, toastError, t],
  );

  const saveQuiz: ICareerLessonMutationsHook['saveQuiz'] = useCallback(
    async (lessonId, questions) => {
      setQuizPending(true);
      try {
        const result = await saveQuizRun({
          variables: { lessonId, questions },
        });
        const payload = result.data?.setCareerLessonQuiz;
        if (payload?.success && payload.lesson) {
          onChanged?.();
          return payload.lesson.quizQuestions;
        }
        toastError(
          t('pages.careerCenter.lessons.toastQuizError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.lessons.toastQuizError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setQuizPending(false);
      }
    },
    [saveQuizRun, onChanged, toastError, t],
  );

  return {
    saveLesson,
    addAttachment,
    removeAttachment,
    clearVideo,
    saveQuiz,
    pending,
    mediaPending,
    quizPending,
  };
};
